/**
 * Rendering Engine — token resolution.
 *
 * Design JSON V2 nodes reference style tokens ("primary", "lg", "display")
 * rather than literal values. This module turns a document's token table into
 * resolvers that produce the concrete values the editor needs, falling back to
 * sane defaults so an under-specified document still renders.
 *
 * Pure and deterministic.
 */

import { TYPE_SCALE, SPACE_FALLBACK, RADIUS_FALLBACK, COLOR_FALLBACK } from './rendererSchema';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const round2 = (n) => Math.round(n * 100) / 100;

/** A literal colour rather than a token name. */
const isLiteralColor = (v) => typeof v === 'string' && (v.startsWith('#') || v.startsWith('rgb') || v.startsWith('hsl') || v === 'transparent' || v === 'none');

/**
 * Build the resolvers for one document.
 *
 * @param {Object} [tokens]  the document's token table.
 * @param {Object} [options]
 * @param {number} [options.scale]  px → canvas-unit factor. Tokens are authored
 *   in px; a mm canvas must convert them, or a 24px pad becomes 24mm.
 * @returns {Object} { color, space, radius, typeStyle, has, table }
 */
/**
 * Line height as a MULTIPLIER, whatever the document meant by it.
 *
 * `leading` is documented as a multiplier and every built-in token is one
 * (1.05–1.5). Models write absolute line heights in px instead — a real
 * document declared `leading: 48` for a 149px display face, which multiplied
 * out to a headline 28,608px tall on a 1350px canvas and pushed every element
 * after it more than 40,000px off the page. The document still validated, still
 * rendered, and still scored 80.
 *
 * A value past LEADING_AS_MULTIPLIER_MAX cannot be a multiplier at any real
 * type size, so it is read as px and divided back into one.
 */
const LEADING_AS_MULTIPLIER_MAX = 3;
const LEADING_FALLBACK = 1.4;

export const resolveLeading = (raw, fontSize) => {
  if (!isNum(raw) || raw <= 0) return LEADING_FALLBACK;
  if (raw <= LEADING_AS_MULTIPLIER_MAX) return raw;
  const derived = isNum(fontSize) && fontSize > 0 ? raw / fontSize : LEADING_FALLBACK;
  return Math.min(LEADING_AS_MULTIPLIER_MAX, Math.max(0.8, derived));
};

export const createTokenResolver = (tokens = {}, { scale = 1, spacingScale = 1 } = {}) => {
  const t = isObj(tokens) ? tokens : {};
  const toCanvas = (px) => (isNum(px) ? px * scale : px);
  const colors = { ...COLOR_FALLBACK, ...(isObj(t.color) ? t.color : {}) };
  const spaces = { ...SPACE_FALLBACK, ...(isObj(t.space) ? t.space : {}) };
  const radii = { ...RADIUS_FALLBACK, ...(isObj(t.radius) ? t.radius : {}) };
  const types = isObj(t.type) ? t.type : {};

  /** Token ref, literal colour, or null → a concrete colour. */
  const color = (ref, fallback = COLOR_FALLBACK.onSurface) => {
    if (isLiteralColor(ref)) return ref;
    if (typeof ref === 'string' && colors[ref]) return colors[ref];
    return fallback;
  };

  /**
   * Token ref or a raw number → a length in CANVAS units.
   * Tokens are authored in px, so a mm canvas converts them.
   */
  /**
   * A spacing token in canvas units.
   *
   * `spacingScale` compresses gaps and padding — and ONLY gaps and padding.
   * When a rich composition wants more height than the page has, the honest
   * first move is to tighten the air between things, not to shrink the
   * headline, the hero or the copy. Type sizes and intrinsic sizes are
   * resolved elsewhere and are deliberately untouched by this.
   */
  const space = (ref, fallback = 0) => {
    const raw = isNum(ref) ? ref : (typeof ref === 'string' && isNum(spaces[ref]) ? spaces[ref] : fallback);
    return toCanvas(raw * spacingScale);
  };

  const radius = (ref, fallback = 0) => {
    if (isNum(ref)) return toCanvas(ref);
    if (Array.isArray(ref)) return ref.map((r) => radius(r, 0));
    if (typeof ref === 'string' && isNum(radii[ref])) return toCanvas(radii[ref]);
    return toCanvas(fallback);
  };

  /** A type-style ref ("display", "body") → the full resolved style. */
  const typeStyle = (ref) => {
    const style = isObj(types[ref]) ? types[ref] : {};
    const step = isNum(style.step) ? style.step : 0;
    // The scale is exponential, so a step maps to a real, consistent size.
    // Clamped in px (the scale's own unit), then converted to canvas units.
    const fontSize = round2(toCanvas(clamp(Math.round(TYPE_SCALE.base * (TYPE_SCALE.ratio ** step)), TYPE_SCALE.min, TYPE_SCALE.max)));
    return {
      font: style.font ?? null,
      weight: isNum(style.weight) ? style.weight : 400,
      fontSize,
      leading: resolveLeading(style.leading, fontSize),
      tracking: isNum(style.tracking) ? style.tracking : 0,
      case: style.case || 'none',
      step,
    };
  };

  return {
    color,
    space,
    radius,
    typeStyle,
    /** True when the document actually defined this token. */
    has: (group, key) => isObj(t[group]) && t[group][key] !== undefined && t[group][key] !== null,
    table: { color: colors, space: spaces, radius: radii, type: types },
  };
};

/** Apply a text case transform. The editor stores the transformed copy. */
export const applyTextCase = (text, textCase) => {
  const value = typeof text === 'string' ? text : '';
  switch (textCase) {
    case 'upper': return value.toUpperCase();
    case 'lower': return value.toLowerCase();
    case 'title': return value.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
    case 'sentence': return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    default: return value;
  }
};
